"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useMemo } from "react";
import { useRouter } from "next/navigation";
import ProductCard from "@/features/cake-customization/ProductCard";
import LoadingState, { EmptyState } from "@/components/ui/loading-state";
import { Product, ProductType } from "@/types/product";
import { ProductCardSkeleton } from "@/components/ui/skeleton";
interface ProductsSectionProps {
  products: Product[];
  isProductsLoading: boolean;
  isProductsError: boolean;
  refetchProducts: () => void;
  isFetchingNextPage: boolean;
  selectedProductType: ProductType;
  isCollection?: boolean;
  isThemeCake?: boolean;
  initialSubTypesData?: any;
  dietaryCategoriesData?: any;
  searchParams?: {
    [key: string]: string | string[] | undefined;
  };
}

/**
 * ProductsSection – Displays a tag filter row and a responsive product grid.
 *
 * The component keeps the selected tag IDs in the URL (tag_ids query param)
 * so filters are shareable and navigable. When a tag is (de)selected we push
 * the new URL without scrolling.
 */
function ProductsSectionComponent(t0) {
  const $ = _c(28);
  const {
    products,
    isProductsLoading,
    isProductsError,
    refetchProducts,
    isFetchingNextPage,
    isThemeCake,
    searchParams: searchParamsProp
  } = t0;
  const router = useRouter();
  let params;
  if ($[0] !== searchParamsProp) {
    params = new URLSearchParams();
    if (searchParamsProp) {
      Object.entries(searchParamsProp).forEach(t1 => {
        const [key, value] = t1;
        if (value !== undefined) {
          const stringValue = Array.isArray(value) ? value[0] : value as string;
          if (stringValue !== undefined) {
            params.set(key, stringValue);
          }
        }
      });
    }
    $[0] = searchParamsProp;
    $[1] = params;
  } else {
    params = $[1];
  }
  const searchParams = params;
  const [columnsState, setColumnsState] = React.useState(3);
  const [isNavigating, setIsNavigating] = React.useState(false);
  let t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const handleStart = () => setIsNavigating(true);
      window.addEventListener("route-change-start", handleStart);
      return () => window.removeEventListener("route-change-start", handleStart);
    };
    t2 = [];
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  React.useEffect(t1, t2);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setIsNavigating(false);
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== searchParamsProp) {
    t4 = [searchParamsProp];
    $[5] = searchParamsProp;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  React.useEffect(t3, t4);
  let t5;
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      const updateColumns = () => {
        if (window.innerWidth >= 768) {
          setColumnsState(3);
        } else {
          setColumnsState(2);
        }
      };
      updateColumns();
      window.addEventListener("resize", updateColumns);
      return () => window.removeEventListener("resize", updateColumns);
    };
    t6 = [];
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  React.useEffect(t5, t6);
  const missing = (columnsState - products.length % columnsState) % columnsState;
  const skeletonCount = missing + columnsState;
  if (isProductsError) {
    let t7;
    if ($[9] !== refetchProducts) {
      t7 = <EmptyState title="Failed to load products" message="We couldn't load the products. Please try again." actionText="Try Again" onAction={refetchProducts} />;
      $[9] = refetchProducts;
      $[10] = t7;
    } else {
      t7 = $[10];
    }
    return t7;
  }
  const showLoading = isProductsLoading || isNavigating;
  let t7;
  if ($[11] !== showLoading) {
    t7 = showLoading && <LoadingState type="products" count={6} />;
    $[11] = showLoading;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== isThemeCake || $[14] !== products.length || $[15] !== router || $[16] !== searchParams || $[17] !== showLoading) {
    t8 = !showLoading && products.length === 0 && <EmptyState title="No products found" message="Try adjusting your filters or check back later." actionText="Clear Filters" onAction={() => {
      window.dispatchEvent(new Event("route-change-start"));
      const isNavbarLabel = selectedProductType?.is_navbar_label || selectedProductType?.id && String(selectedProductType.id).startsWith("label-");
      const cleanId = selectedProductType?.id ? String(selectedProductType.id).replace("label-", "") : null;
      if (isThemeCake) {
        router.push("?", {
          scroll: false
        });
      } else {
        if (isNavbarLabel && cleanId) {
          router.push(`?category_id=${cleanId}`, {
            scroll: false
          });
        } else {
          if (cleanId) {
            router.push(`?id=${cleanId}`, {
              scroll: false
            });
          } else {
            const params_0 = new URLSearchParams(searchParams.toString() || "");
            const id = params_0.get("id");
            const categoryId = params_0.get("category_id");
            params_0.forEach((_, key_0) => {
              params_0.delete(key_0);
            });
            if (id) {
              params_0.set("id", id);
            }
            if (categoryId) {
              params_0.set("category_id", categoryId);
            }
            const query = params_0.toString() ? `?${params_0.toString()}` : "?";
            router.push(query, {
              scroll: false
            });
          }
        }
      }
    }} />;
    $[13] = isThemeCake;
    $[14] = products.length;
    $[15] = router;
    $[16] = searchParams;
    $[17] = showLoading;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== isFetchingNextPage || $[20] !== products || $[21] !== showLoading || $[22] !== skeletonCount) {
    t9 = !showLoading && <div className="grid w-full grid-cols-2 md:grid-cols-3 lg:grid-cols-3 md:gap-7 gap-1 max-lg:pb-12">{products.map(_temp)}{isFetchingNextPage && Array.from({
        length: skeletonCount
      }).map(_temp2)}</div>;
    $[19] = isFetchingNextPage;
    $[20] = products;
    $[21] = showLoading;
    $[22] = skeletonCount;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t7 || $[25] !== t8 || $[26] !== t9) {
    t10 = <section className=" mb-4 pb-4 lg:mb-6 lg:pb-6">{t7}{t8}{t9}</section>;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  return t10;
}
function _temp2(__0, i) {
  return <ProductCardSkeleton key={`skeleton-${i}`} />;
}
function _temp(product) {
  if (!product) {
    return null;
  }
  return <ProductCard key={product.id} product={product} />;
}
export default ProductsSectionComponent;